import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import Sidepanel from 'modules/core/components/Sidepanel'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type { Shift } from 'modules/shift_management/lib/types'

import CustomShiftInformation from 'modules/shift_management/components/ShiftManagement/CustomShiftInformation'
import SavedShiftInformation from 'modules/shift_management/components/ShiftManagement/SavedShiftInformation'
import BasicInformation from './BasicInformation'
import ExtraInformation from './ExtraInformation'
import { CanManageFooter, ReadOnlyFooter } from './FooterOptions'

export interface Props {
  shift: Shift
  readOnly: boolean
  onOpenEditSidepanel: () => void
  onClose: () => void
}

const SeeShiftSidepanel = ({ shift, readOnly, onOpenEditSidepanel, onClose }: Props) => {
  const employeeName = shift.employeeFullName
  const date = dateToString(shift.date, 'ui_long_with_year')

  const isOvertime = shift.isOvertime
  const hasLocation = !!shift.location
  const hasWorkArea = !!shift.workArea
  const hasNotes = !!shift.notes

  const plannedBreaks = shift.plannedBreaks.plannedBreaks
  const hasBreaks = !!plannedBreaks.length

  const hasExtraInformation = isOvertime || hasBreaks || hasLocation || hasWorkArea || hasNotes

  const [hasSeenBackupShiftsCallout, setHasSeenBackupShiftsCallout] = useState<boolean>(false)

  const canEdit = !readOnly && !shift.isBackup
  const footer = canEdit ? (
    <CanManageFooter onClose={onClose} onOpenEditSidepanel={onOpenEditSidepanel} />
  ) : (
    <ReadOnlyFooter onClose={onClose} />
  )

  return (
    <Sidepanel
      open
      title={i18n.t('shift_management.shift_details_modal.title')}
      footer={footer}
      onClose={onClose}
      closeLabel="Close"
    >
      <Box gap="s24">
        {shift.isBackup && !hasSeenBackupShiftsCallout && (
          <Callout
            title={i18n.t('shift_management.backup_shifts.title')}
            description={i18n.t('shift_management.backup_shifts.description')}
            type="info"
            onRemove={() => setHasSeenBackupShiftsCallout(true)}
          />
        )}
        <BasicInformation employeeName={employeeName ?? ''} date={date} />
        {shift.isDefault || shift.isFromTemplate ? (
          <Box
            gap="s16"
            padding="s12"
            borderRadius={{ all: 'abs016' }}
            border={{
              all: {
                style: 'solid',
                width: 's2',
                color: 'grey400',
              },
            }}
          >
            <Box flexDirection="row">
              <SavedShiftInformation
                title={shift.displayName}
                color={shift.color}
                startAt={shift.startTime}
                endAt={shift.endTime}
                overnightShift={shift.isNightShift}
                timezone={shift.timezone}
              />
            </Box>

            {hasExtraInformation && (
              <ExtraInformation shift={shift} plannedBreaks={plannedBreaks} />
            )}
          </Box>
        ) : (
          <>
            <CustomShiftInformation shift={shift} />
            {hasExtraInformation && (
              <ExtraInformation shift={shift} plannedBreaks={plannedBreaks} fromCustom />
            )}
          </>
        )}
      </Box>
    </Sidepanel>
  )
}

export default observer(SeeShiftSidepanel)
